import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { CheckCircle, AlertTriangle, Info, X } from 'lucide-react';

/**
 * Globális toast-rendszer – a néma mentések és a window.alert()-ek kiváltása.
 *
 * Használat:
 *   const toast = useToast();
 *   toast.success('Mentve');
 *   toast.error('A mentés sikertelen: ' + msg);
 *   toast.info('Szinkron elindítva');
 *
 * A Provider az App gyökerében ül; a toastok jobb felül jelennek meg,
 * success/info 3,5 mp után tűnik el (kézzel is zárható).
 *
 * ⚠ K-12 (2026-09-27, Gábor szabálya): HIBA SOHA NEM ZÁR MAGÁTÓL — az error-toast csak a
 * × gombbal tűnik el, `role="alert"`-tel jelenik meg (a képernyőolvasó azonnal felolvassa),
 * és egy siker-toast sem szoríthatja ki: hibából a legutóbbi 5, nem-hibából a legutóbbi 2 látszik.
 * Mentési hibára az új felületeken inline, tartós üzenet a szabály (INV-37); a toast a
 * meglévő hívóhelyek hibáit viszi.
 */
const ToastCtx = createContext(null);

export function useToast() {
  const ctx = useContext(ToastCtx);
  // Provider nélkül (pl. tesztben) ne dobjon – csendes no-op
  return ctx || { success: () => {}, error: () => {}, info: () => {} };
}

const VARIANTS = {
  success: { Icon: CheckCircle,   color: 'var(--accent)',  border: 'rgba(16,185,129,0.4)' },
  error:   { Icon: AlertTriangle, color: 'var(--danger)',  border: 'rgba(239,68,68,0.4)' },
  info:    { Icon: Info,          color: 'var(--primary)', border: 'rgba(59,130,246,0.4)' },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const remove = useCallback((id) => {
    setToasts(t => t.filter(x => x.id !== id));
  }, []);

  const push = useCallback((type, msg) => {
    const id = ++idRef.current;
    setToasts(t => {
      // Hibából a legutóbbi 5, nem-hibából a legutóbbi 2 — a kettő egymást nem szorítja ki
      // (review 2026-09-28: 5 nyitott hiba mellett a „Mentve” azonnal eltűnt volna).
      const next = [...t, { id, type, msg }];
      const errs = next.filter(x => x.type === 'error').slice(-5);
      const rest = next.filter(x => x.type !== 'error').slice(-2);
      const keep = new Set([...errs, ...rest].map(x => x.id));
      return next.filter(x => keep.has(x.id));
    });
    if (type !== 'error') setTimeout(() => remove(id), 3500);
  }, [remove]);

  const api = useMemo(() => ({
    success: (m) => push('success', m),
    error:   (m) => push('error', m),
    info:    (m) => push('info', m),
  }), [push]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div aria-live="polite" style={{position:'fixed',top:14,right:14,zIndex:2000,
        display:'flex',flexDirection:'column',gap:8,maxWidth:360,pointerEvents:'none'}}>
        {toasts.map(t => {
          const v = VARIANTS[t.type] || VARIANTS.info;
          const { Icon } = v;
          return (
            <div key={t.id} role={t.type === 'error' ? 'alert' : 'status'}
              style={{display:'flex',alignItems:'flex-start',
              gap:9,background:'var(--bg-card)',border:`1px solid ${v.border}`,
              borderRadius:'var(--radius)',boxShadow:'var(--shadow)',
              padding:'0.6rem 0.75rem',pointerEvents:'auto'}}>
              <Icon size={15} style={{color:v.color,flexShrink:0,marginTop:1}}/>
              <div style={{fontSize:'0.8rem',color:'var(--text)',lineHeight:1.45,
                wordBreak:'break-word'}}>{t.msg}</div>
              <button onClick={() => remove(t.id)} aria-label="Bezárás"
                style={{background:'none',border:'none',color:'var(--text-muted)',
                cursor:'pointer',padding:0,marginLeft:'auto',lineHeight:1,flexShrink:0}}>
                <X size={13}/>
              </button>
            </div>
          );
        })}
      </div>
    </ToastCtx.Provider>
  );
}
